import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import useCustomSelector from "@redux/hooks/useCustomSelector";

import {
  InputTextVertical,
  SelectControllerVertical,
  VerticalInputFormLayout,
} from "@components";

import { getConvertedOfficeData } from "@helpers/inventory/metaDataConvert";

import { useLanguage } from "@hooks/useLanguage";
import useActionCreator from "../../../../../../redux/hooks/useActionCreator";

import {
  BuildingCreateType,
  BuildingDetailsResponseType,
} from "@type/inventory/building";
import { useBuildingById } from "@hooks/inventory/roomConfiguration/building/useBuildingDetails";
import { useOfficeList } from "@hooks/inventory/roomConfiguration/useOfficeList";
import { OfficeResponseType } from "@type/inventory/office";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  roomConfigBuildingValidationSchema,
  ROOM_CONFIG_BUILDING,
} from "@validations/Inventory/roomConfig/buildingTab/roomConfigBuildingValidation";

interface BuildingFormProps {
  setIsSuccessModalOpen: (arg: boolean) => void;
  setIsErrorModalOpen: (arg: boolean) => void;
  setErrorMessage: (err: string) => void;
}

export default function BuildingForm({
  setIsSuccessModalOpen,
  setIsErrorModalOpen,
  setErrorMessage,
}: BuildingFormProps) {
  const [buildingId, setBuildingId] = useState<number | string>("");

  const { language } = useLanguage();

  const {
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<BuildingCreateType>({
    resolver: yupResolver(roomConfigBuildingValidationSchema),
  });

  const { roomConfigOfficeState } = useOfficeList(true);

  const { getData } = roomConfigOfficeState;

  const { data: offices } = getData || ({} as OfficeResponseType);

  const { createBuilding, updateBuilding } = useActionCreator();

  const { buildingState } = useBuildingById(false);

  const { getSingleData } = buildingState;

  const { data: building } =
    getSingleData || ({} as BuildingDetailsResponseType);

  const resetAction = () => {
    reset();
    setBuildingId("");
  };

  const onSubmit: SubmitHandler<BuildingCreateType> = (data) => {
    if (buildingId) {
      updateBuilding(buildingId, {
        ...data,
        floorCount: Number(data.floorCount),
      });
    } else {
      createBuilding({
        ...data,
        floorCount: Number(data.floorCount),
      });
    }
  };

  const { createSuccess, createError, updateError, updateSuccess } =
    useCustomSelector((store) => store.inventory.roomConfigBuildingState);

  useEffect(() => {
    if (createSuccess || updateSuccess) {
      reset();
      if (updateSuccess) {
        setBuildingId("");
      }
      setIsSuccessModalOpen(createSuccess || updateSuccess);

      if (createError?.response && createError?.response?.data) {
        setErrorMessage(
          createError.response.data.message || "something was wrong",
        );
      }

      if (updateError?.response && updateError?.response?.data) {
        setErrorMessage(
          updateError.response.data.message || "something was wrong",
        );
      }
    }
    setIsErrorModalOpen(createError || updateError);
  }, [
    createSuccess,
    createError,
    updateError,
    updateSuccess,
    reset,
    setIsSuccessModalOpen,
    setIsErrorModalOpen,
    setErrorMessage,
  ]);

  useEffect(() => {
    if (building) {
      setValue("officeAddressId", building.officeAddress?.id || 0);
      setValue("buildingName", building.buildingName);
      setValue("floorCount", building.floorCount);
      setBuildingId(building.id);
    }
  }, [building, setValue]);

  useEffect(() => resetAction(), []);

  return (
    <VerticalInputFormLayout
      label="INVENTORY_ROOM_CONFIG.BUILDING.FORM.HEADER"
      handleSubmit={handleSubmit}
      onSubmit={onSubmit}
      isUpdate={buildingId ? true : false}
      cancelAction={resetAction}
    >
      <SelectControllerVertical
        label="INVENTORY_ROOM_CONFIG.BUILDING.FORM.FIELDS.OFFICE_LOCATION"
        name="officeAddressId"
        register={{
          ...register(ROOM_CONFIG_BUILDING.OFFICE),
        }}
        options={getConvertedOfficeData(offices, language)}
        setValue={setValue}
        control={control}
        error={errors}
        required
        cols={3}
      />
      <InputTextVertical
        label="INVENTORY_ROOM_CONFIG.BUILDING.FORM.FIELDS.BUILDING_NAME"
        register={{
          ...register(ROOM_CONFIG_BUILDING.BUILDING),
        }}
        error={errors}
        required
        cols={3}
      />
      <InputTextVertical
        label="INVENTORY_ROOM_CONFIG.BUILDING.FORM.FIELDS.FLOOR_NO"
        register={{
          ...register(ROOM_CONFIG_BUILDING.FLOOR),
        }}
        type="number"
        error={errors}
        required
        cols={3}
      />
    </VerticalInputFormLayout>
  );
}
